import { Field } from 'formik'
import React, { useEffect } from 'react'

import { ButtonClean, Fieldset, Input } from '@fourthwall/components'

import type { SelectOption } from '../../../../types'
import type { FormValues } from '../../../../utils/FormValues'
import { OptionsDescription, OptionsSection, OptionsTitle } from './forms.styled'

type SelectFormProps = {
  setFieldValue: (field: string, value: any) => void
  values: FormValues
}

export const SelectForm = ({ setFieldValue, values }: SelectFormProps) => {
  const options = values.options || []

  // Ensure there's at least one option
  useEffect(() => {
    if (options.length === 0) {
      setFieldValue('options', [{ value: '', label: '', group: '' }])
    }
  }, [options.length, setFieldValue])

  const handleOptionAdd = () => {
    setFieldValue('options', [...options, { value: '', label: '', group: '' }])
  }

  const handleOptionRemove = (optionIndex: number) => {
    // Don't allow removing the last option
    if (options.length <= 1) return

    const newOptions = [...options]
    newOptions.splice(optionIndex, 1)
    setFieldValue('options', newOptions)
  }

  const renderOptions = () => {
    return options.map((option: SelectOption, index: number) => (
      <Fieldset.Row key={index}>
        <Fieldset.Item>
          <Field
            name={`options[${index}].value`}
            type="text"
            component={Input}
            label={`Option ${index + 1} Value`}
            defaultValue={option.value}
          />
        </Fieldset.Item>
        <Fieldset.Item>
          <Field
            name={`options[${index}].label`}
            type="text"
            component={Input}
            label={`Option ${index + 1} Label`}
            defaultValue={option.label}
          />
        </Fieldset.Item>
        <Fieldset.Item>
          <Field
            name={`options[${index}].group`}
            type="text"
            component={Input}
            label={`Option ${index + 1} Group (optional)`}
            defaultValue={option.group}
          />
        </Fieldset.Item>
        <Fieldset.Item>
          <ButtonClean label="Remove" onClick={() => handleOptionRemove(index)} />
        </Fieldset.Item>
      </Fieldset.Row>
    ))
  }

  return (
    <>
      <Fieldset.Row>
        <Fieldset.Item>
          <Field name="label" component={Input} label="Label" placeholder="Setting label" />
        </Fieldset.Item>
      </Fieldset.Row>
      <Fieldset.Row>
        <Fieldset.Item>
          <Field
            name="info"
            component={Input}
            label="Info (optional)"
            placeholder="Help text for this setting"
          />
        </Fieldset.Item>
      </Fieldset.Row>
      <Fieldset.Row>
        <Fieldset.Item>
          <Field
            name="default"
            component={Input}
            label="Default Value (optional)"
            placeholder="Default value"
          />
        </Fieldset.Item>
      </Fieldset.Row>

      {/* Options Section */}
      <Fieldset.Row>
        <Fieldset.Item>
          <OptionsSection>
            <OptionsTitle>Options</OptionsTitle>
            <OptionsDescription>
              Add the available options for this select field. Groups are optional and can be used
              to organize options.
            </OptionsDescription>
          </OptionsSection>
        </Fieldset.Item>
      </Fieldset.Row>

      {renderOptions()}

      <Fieldset.Row>
        <Fieldset.Item>
          <ButtonClean onClick={handleOptionAdd} label="Add Option" />
        </Fieldset.Item>
      </Fieldset.Row>
    </>
  )
}
